import { FirebaseError, initializeApp } from "firebase/app";
import { connectAuthEmulator, getAuth } from "firebase/auth";
import { connectFirestoreEmulator, getFirestore } from "firebase/firestore";
import { connectFunctionsEmulator, getFunctions } from "firebase/functions";
import { connectStorageEmulator, getStorage } from "firebase/storage";

const firebaseConfig = {
  apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
  authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
  storageBucket: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID,
};

const app = initializeApp(firebaseConfig);

const auth = getAuth(app);
const firestore = getFirestore(app);
const functions = getFunctions(app);
const storage = getStorage(app);

const shouldUseFirebaseEmulator =
  process.env.NEXT_PUBLIC_USE_FIREBASE_EMULATOR === "true";
const shouldUseAuthEmulator =
  process.env.NEXT_PUBLIC_USE_AUTH_EMULATOR
    ? process.env.NEXT_PUBLIC_USE_AUTH_EMULATOR === "true"
    : shouldUseFirebaseEmulator;

if (typeof window !== "undefined" && shouldUseFirebaseEmulator) {
  const emulatorHost = process.env.NEXT_PUBLIC_FIREBASE_EMULATOR_HOST || "127.0.0.1";
  const firestorePort = Number(process.env.NEXT_PUBLIC_FIRESTORE_EMULATOR_PORT || "8080");
  const functionsPort = Number(process.env.NEXT_PUBLIC_FUNCTIONS_EMULATOR_PORT || "5001");
  const storagePort = Number(process.env.NEXT_PUBLIC_STORAGE_EMULATOR_PORT || "9199");

  try {
    connectFirestoreEmulator(firestore, emulatorHost, firestorePort);
  } catch (error) {
    if (!(error instanceof Error) || !error.message.includes("already been started")) {
      throw error;
    }
  }

  try {
    connectFunctionsEmulator(functions, emulatorHost, functionsPort);
  } catch (error) {
    if (!(error instanceof Error) || !error.message.includes("already been started")) {
      throw error;
    }
  }

  try {
    connectStorageEmulator(storage, emulatorHost, storagePort);
  } catch (error) {
    if (!(error instanceof Error) || !error.message.includes("already been started")) {
      throw error;
    }
  }
}

if (typeof window !== "undefined" && shouldUseAuthEmulator) {
  const emulatorHost = process.env.NEXT_PUBLIC_FIREBASE_EMULATOR_HOST || "127.0.0.1";
  const authPort = Number(process.env.NEXT_PUBLIC_AUTH_EMULATOR_PORT || "9099");

  try {
    connectAuthEmulator(auth, `http://${emulatorHost}:${authPort}`, {
      disableWarnings: true,
    });
  } catch (error) {
    if (!(error instanceof Error) || !error.message.includes("already been started")) {
      throw error;
    }
  }
}

/**
 * Firebase Authenticationエラーコードに基づくエラーメッセージを返す関数
 * @param {FirebaseError} error - FirebaseError オブジェクト
 * @returns {string} - エラーメッセージ
 */
const handleFirebaseError = (
  error: any,
  defaultMsg: string = "予期しないエラーが発生しました。後でもう一度お試しください。"
) => {
  if (error instanceof FirebaseError) {
    switch (error.code) {
      case "auth/email-already-in-use":
        return "このメールアドレスはすでに使用されています。別のメールアドレスをご利用ください。";
      case "auth/invalid-email":
        return "メールアドレスが無効です。確認して再度お試しください。";
      case "auth/weak-password":
        return "パスワードは最低でも6文字以上で、大文字、小文字、数字、特殊文字を含むとより強力です。";
      case "auth/user-disabled":
        return "このアカウントは無効化されています。サポートにご連絡ください。";
      case "auth/user-not-found":
        return "このメールアドレスでユーザーが見つかりませんでした。確認して再度お試しください。";
      case "auth/expired-action-code":
        return "リンクの有効期限が切れています。もう一度パスワード再設定のリクエストを送信してください。";
      case "auth/invalid-action-code":
        return "無効なリンクです。もう一度パスワード再設定のリクエストを送信してください。";
      case "auth/invalid-password":
        return "パスワードが無効です。適切なパスワードを入力してください。";
      default:
        return defaultMsg;
    }
  } else {
    return defaultMsg;
  }
};

export { app, auth, firestore, functions, handleFirebaseError, storage };
